import React, { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { KpiIcon } from "../../KpiIcons";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config.json";
import {
  isOk,
  isSpreadsheet,
  parseDate,
  getCol,
  fmt,
} from "./utils";
import Fld from "./FieldLabel";
import FileUploadZone from "./FileUploadZone";
import DocumentViewerPanel from "./DocumentViewerPanel";
import HistoryModal from "./HistoryModal";

/* ─────────────────────────────────────────────────────────────
   Spark bar (for emission / energy YoY-style rows)
───────────────────────────────────────────────────────────── */
const SparkBar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.4s ease" }} />
      </div>
      <span style={{ fontSize: 9.5, fontFamily: "monospace", color: "#94a3b8", minWidth: 28, textAlign: "right" }}>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Compact emission / energy inline row (same style as KpiRow)
───────────────────────────────────────────────────────────── */
const CompactTypeRow = ({ icon, label, value, unit, accentColor, bgColor, borderColor }) => {
  const val = Number(value) || 0;
  const hasData = value !== null && value !== undefined;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 0,
      background: bgColor, borderRadius: 8,
      border: `1px solid ${borderColor}`, overflow: "hidden",
    }}>
      {/* Label badge */}
      <div style={{
        display: "flex", alignItems: "center", gap: 5,
        padding: "9px 14px", minWidth: 120, flexShrink: 0,
        borderRight: `1px solid ${borderColor}`,
      }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: accentColor, textTransform: "uppercase", letterSpacing: "0.07em" }}>
          {label}
        </span>
      </div>

      {/* Value */}
      <div style={{ flex: 1, padding: "9px 16px" }}>
        {hasData ? (
          <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: accentColor, fontFamily: "monospace", letterSpacing: "-0.02em" }}>
              {val}
            </span>
            <span style={{ fontSize: 10, color: accentColor, opacity: 0.7, textTransform: "uppercase" }}>
              {unit}
            </span>
            <div style={{ flex: 1, marginLeft: 12 }}>
              <SparkBar value={val} max={Math.max(val, 1)} color={accentColor} />
            </div>
          </div>
        ) : (
          <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>No data</span>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const AccordionPanel = ({
  answer,
  locs,
  questionId,
  financialYearId,
  onSave,
  onCancel,
  canEdit,
  onChat,
  unitSetting,
  onOpenAuditTrail,
}) => {
  const resolvedUnits = unitSetting?.supportedUnits?.length
    ? unitSetting.supportedUnits.map((u) => u.unit)
    : [];

  const defaultUnit =
    unitSetting?.selectedUnit ||
    unitSetting?.baseUnit ||
    answer.unit ||
    resolvedUnits[0] ||
    "";

  const [editMode, setEditMode] = useState(false);
  const [showHistModal, setShowHistModal] = useState(false);

  const [val, setVal]   = useState(String(answer.value ?? ""));
  const [unit, setUnit] = useState(answer.unit ?? defaultUnit);
  const [loc, setLoc]   = useState(String(answer.location_id ?? answer.locationId ?? ""));
  const [from, setFrom] = useState((answer.from_date || answer.fromDate || "")?.slice(0, 10));
  const [to, setTo]     = useState((answer.to_date   || answer.toDate   || "")?.slice(0, 10));
  const [note, setNote] = useState(answer.note ?? answer.remarks ?? "");
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadedMeta, setUploadedMeta] = useState(null);
  const [uploadErr, setUploadErr] = useState("");

  /* ── location: robust lookup ── */
  const locObj = (() => {
    const lid = loc || answer.location_id || answer.locationId;
    if (!lid) return null;
    return locs.find((l) => String(l.id) === String(lid)) || null;
  })();

  /* ── emission / energy from API response ── */
  const emissionVal  = answer.emission_value  ?? null;
  const emissionUnit = answer.emission_unit   || "kgCO2e";
  const energyVal    = answer.energy_value    ?? null;
  const energyUnit   = answer.energy_unit     || "GJ";

  const hasEmission = emissionVal !== null;
  const hasEnergy   = energyVal   !== null;

  // ── File handler ─────────────────────────────────────────────────────────
  const handleFile = useCallback(async (file) => {
    setUploadErr("");
    setUploadedMeta(null);
    if (isSpreadsheet(file)) {
      setUploading(true); setUploadPct(15);
      try {
        const buf = await file.arrayBuffer();
        setUploadPct(55);
        const wb   = XLSX.read(buf);
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
        setUploadPct(85);
        if (rows.length) {
          const row = rows[0];
          const rv  = getCol(row, "value");
          const rf  = parseDate(getCol(row, "from_date"));
          const rt  = parseDate(getCol(row, "to_date"));
          const ru  = getCol(row, "unit", "units");
          const rl  = getCol(row, "location", "source", "unit_code", "unitcode", "site");
          const lm  = rl ? locs.find((l) => l.name?.toLowerCase() === rl.toLowerCase()) : null;
          if (rv) setVal(rv);
          if (rf) setFrom(rf);
          if (rt) setTo(rt);
          if (ru && resolvedUnits.includes(ru)) setUnit(ru);
          if (lm) setLoc(String(lm.id));
        }
        setUploadPct(100);
        await new Promise((r) => setTimeout(r, 120));
        setUploadedMeta({ source: "xlsx", fileName: file.name, fileSize: file.size, fileMetadataId: null });
      } catch { setUploadErr("Could not read this spreadsheet."); }
      finally { setUploading(false); setUploadPct(0); }
    } else {
      setUploading(true); setUploadPct(20);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("shouldParseDocument", "false");
        setUploadPct(45);
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}documents/upload`, {}, fd, "POST"
        );
        setUploadPct(90);
        if (!isSuccess || !data) { setUploadErr("Server could not upload this file."); return; }
        await new Promise((r) => setTimeout(r, 120));
        setUploadedMeta({
          source: "api",
          fileMetadataId: data.fileMetadataId,
          documentType: data.documentType,
          documentMetadata: data.documentMetadata ?? {},
          fileName: data.fileMetadata?.fileName ?? file.name,
          fileSize: data.fileMetadata?.fileSize ?? file.size,
          fileMetadata: data.fileMetadata,
          isExisting: data.isExisting,
        });
      } catch { setUploadErr("Upload failed — check your connection."); }
      finally { setUploading(false); setUploadPct(0); }
    }
  }, [locs, resolvedUnits]);

  // ── Save ─────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const payload = {
        value: Number(val), unit,
        location_id: loc ? Number(loc) : undefined,
        from_date: from || undefined,
        to_date: to || undefined,
        ...(uploadedMeta?.fileMetadataId ? {
          uploadedFileMetadata: {
            documentType: uploadedMeta.documentType,
            fileMetadataId: uploadedMeta.fileMetadataId,
            fileMetadata: uploadedMeta.fileMetadata,
            isExisting: uploadedMeta.isExisting,
          },
        } : {}),
      };
      const res = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/${answer.id}`, {}, payload, "PUT"
      );
      if (isOk(res)) {
        setMsg({ ok: true, t: "Saved successfully" });
        setEditMode(false); onSave?.();
        setTimeout(() => setMsg(null), 3000);
      } else {
        setMsg({ ok: false, t: res?.data?.message || "Save failed" });
      }
    } catch { setMsg({ ok: false, t: "Network error" }); }
    setSaving(false);
  };

  // ── Cancel edit ──────────────────────────────────────────────────────────
  const cancelEdit = () => {
    setEditMode(false);
    setVal(String(answer.value ?? ""));
    setUnit(answer.unit ?? defaultUnit);
    setLoc(String(answer.location_id ?? answer.locationId ?? ""));
    setFrom((answer.from_date || answer.fromDate || "")?.slice(0, 10));
    setTo((answer.to_date || answer.toDate || "")?.slice(0, 10));
    setNote(answer.note ?? answer.remarks ?? "");
    setUploadedMeta(null); setUploadErr(""); setMsg(null);
    onCancel?.();
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="ahp-down"
      style={{
        padding: "20px 24px 24px",
        background: "linear-gradient(135deg,#f8faff 0%,#f1f5fd 100%)",
        borderTop: "2px solid var(--blue2)",
      }}
    >
      {/* ── Action bar ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 20, paddingBottom: 14,
        borderBottom: "1px solid var(--bdr2)", flexWrap: "wrap",
      }}>
        {canEdit && (!editMode ? (
          <button className="ahp-btn ahp-btn-amber ahp-btn-sm" onClick={() => setEditMode(true)}>
            <KpiIcon name="audit" size={12} color="var(--amber)" /> Edit Answer
          </button>
        ) : (
          <>
            <button className="ahp-btn ahp-btn-p ahp-btn-sm" onClick={save} disabled={saving || uploading}>
              {saving
                ? <><span className="ahp-spin" style={{ width: 11, height: 11, border: "2px solid #ffffff44", borderTopColor: "#fff" }} /> Saving…</>
                : <><KpiIcon name="check" size={12} color="#fff" /> Save Changes</>}
            </button>
            <button className="ahp-btn ahp-btn-g ahp-btn-sm" onClick={cancelEdit}>Cancel</button>
          </>
        ))}

        <div style={{ width: 1, height: 20, background: "var(--bdr)", flexShrink: 0 }} />

        <button className="ahp-btn ahp-btn-g ahp-btn-sm" onClick={() => setShowHistModal(true)}>
          <KpiIcon name="history" size={12} color="var(--ink3)" /> View History
        </button>

        <button
          className="ahp-btn ahp-btn-g ahp-btn-sm"
          onClick={() => onOpenAuditTrail?.()}
          title="View audit trail for this answer"
          style={{ display: "inline-flex", alignItems: "center", gap: 5, borderColor: "var(--blue2)", color: "var(--blue)", background: "var(--blue3)" }}
        >
          <KpiIcon name="audit" size={12} color="var(--blue)" /> Audit Trail
        </button>

        {msg && (
          <span style={{ fontSize: 11.5, fontWeight: 600, color: msg.ok ? "var(--green)" : "var(--red)" }}>
            {msg.ok ? "✓" : "⚠"} {msg.t}
          </span>
        )}

        {!editMode && (
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, color: "var(--ink4)", fontWeight: 600 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--amber)", display: "inline-block", flexShrink: 0 }} />
            View only · click Edit Answer to make changes
          </span>
        )}
      </div>

      {/* ── Content ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {/* ── Document ── */}
        <div>
          <label className="ahp-lbl" style={{ marginBottom: 8 }}>
            {editMode ? "Replace Document" : "Attached Document"}
            {editMode && (
              <span style={{ fontSize: 9, fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--ink4)", marginLeft: 6 }}>
                XLSX auto-fills fields · PDF/DOC/image stored as proof
              </span>
            )}
          </label>
          {!editMode ? (
            <DocumentViewerPanel document={answer.document} />
          ) : (
            <>
              <FileUploadZone
                onFile={handleFile}
                uploading={uploading}
                uploadPct={uploadPct}
                uploadedMeta={uploadedMeta}
                onClear={() => { setUploadedMeta(null); setUploadErr(""); }}
                disabled={false}
              />
              {uploadErr && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 7, padding: "8px 12px", borderRadius: 7, background: "var(--red2)", border: "1px solid #fca5a5", fontSize: 11.5, color: "var(--red)", fontWeight: 600 }}>
                  <KpiIcon name="x" size={12} color="var(--red)" /> {uploadErr}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Divider ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
          <span style={{ fontSize: 9.5, color: "var(--ink4)", fontFamily: "var(--fm)", letterSpacing: ".06em" }}>DATA FIELDS</span>
          <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
        </div>

        {/* ── Editable fields ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 1fr", gap: 12 }}>
          <Fld label="Value">
            <input className="ahp-input" type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Enter value…" disabled={!editMode} />
          </Fld>
          <Fld label="Unit">
            <select className="ahp-sel ahp-input" value={unit} onChange={(e) => setUnit(e.target.value)} style={{ width: "100%" }} disabled={!editMode}>
              <option value="">—</option>
              {resolvedUnits.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Fld>
          <Fld label="Location">
            <select className="ahp-sel ahp-input" value={loc} onChange={(e) => setLoc(e.target.value)} style={{ width: "100%" }} disabled={!editMode}>
              <option value="">— No location —</option>
              {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Fld>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Fld label="From Date">
            <input className="ahp-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={!editMode} />
          </Fld>
          <Fld label="To Date">
            <input className="ahp-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={!editMode} />
          </Fld>
          <Fld label="Notes">
            <input className="ahp-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note…" disabled={!editMode} />
          </Fld>
        </div>

        {/* ── Summary strip with location fix ── */}
        <div style={{ display: "flex", gap: 24, padding: "10px 14px", borderRadius: 9, background: "var(--card)", border: "1px solid var(--bdr)", flexWrap: "wrap" }}>
          {[
            { l: "Value",      v: val  ? `${val} ${unit || ""}`.trim() : "—" },
            { l: "Date Range", v: from && to ? `${fmt(from)} → ${fmt(to)}` : "—" },
            { l: "Location",   v: locObj?.name || locObj?.unitCode || (locs.length === 0 ? "Loading…" : "—") },
          ].map(({ l, v }) => (
            <div key={l}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "var(--ink4)", textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--fm)" }}>{l}</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)", fontFamily: "var(--fm)", marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>

        {/* ── Emission / Energy section ── */}
        {(hasEmission || hasEnergy) && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
              <span style={{ fontSize: 9.5, color: "var(--ink4)", fontFamily: "var(--fm)", letterSpacing: ".06em" }}>CALCULATED VALUES</span>
              <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {hasEmission && (
                <CompactTypeRow
                  icon="🌿"
                  label="Emission"
                  value={emissionVal}
                  unit={emissionUnit}
                  accentColor="#15803d"
                  bgColor="#f0fdf4"
                  borderColor="#bbf7d0"
                />
              )}
              {hasEnergy && (
                <CompactTypeRow
                  icon="⚡"
                  label="Energy"
                  value={energyVal}
                  unit={energyUnit}
                  accentColor="#b45309"
                  bgColor="#fffbeb"
                  borderColor="#fde68a"
                />
              )}
            </div>
          </>
        )}

        {/* ── Bottom save row (edit mode only) ── */}
        {editMode && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 4, borderTop: "1px solid var(--bdr2)" }}>
            <button className="ahp-btn ahp-btn-p" onClick={save} disabled={saving || uploading}>
              {saving
                ? <><span className="ahp-spin" style={{ width: 12, height: 12, border: "2px solid #ffffff44", borderTopColor: "#fff" }} /> Saving…</>
                : <><KpiIcon name="check" size={13} color="#fff" /> Save Changes</>}
            </button>
            <button className="ahp-btn ahp-btn-g" onClick={cancelEdit}>Cancel</button>
            {msg && (
              <span style={{ fontSize: 11.5, fontWeight: 600, color: msg.ok ? "var(--green)" : "var(--red)" }}>
                {msg.ok ? "✓" : "⚠"} {msg.t}
              </span>
            )}
          </div>
        )}
      </div>

      {showHistModal && (
        <HistoryModal answer={answer} locs={locs} questionId={questionId} onClose={() => setShowHistModal(false)} />
      )}
    </div>
  );
};

export default AccordionPanel;